import React, { useMemo, useState } from 'react';
import { Button, Drawer, Grid, Space } from 'antd';
import { FilterOutlined } from '@ant-design/icons';

import { useCompaniesData } from '../../../../../Hooks/TT_ELD/Companies';
import CompaniesTable from './CompaniesTable';
import SearchInput from '../../../../../Utils/SearchInput';
import useRouteQuery from '../../../../../Utils/useRouterQuery';
import SearchOptions from '../../../../../Utils/SearchOptions';
import { SearchForOwnerDispatchers } from '../../../../../Utils/SearchResults';
import { useTagQuerySync } from '../../../../../Utils/useTagQuerySync';

const { useBreakpoint } = Grid;

const DisputedCompanies = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [company, setCompany] = useRouteQuery('', 'company', (v) => String(v));
  const [usdot, setUsdot] = useRouteQuery('', 'usdot', (v) => String(v));
  const [uid, setUid] = useRouteQuery('', 'uid', (v) => String(v));
  const [skip, setSkip] = useState<string>('1');

  const [ownerId, setOwnerId] = useState('');
  const [isDispute] = useState<string>('true');

  const [filtersOpen, setFiltersOpen] = useState(false);

  useTagQuerySync(
    { en_US: 'Disputed Companies', zh_CN: '' },
    'disputed-companies',
    { company, usdot, uid, ownerId, skip },
    {
      company: setCompany,
      usdot: setUsdot,
      uid: setUid,
      // ownerId queryda bo‘lsin desang keyin qo‘shamiz (hozir state)
      skip: setSkip,
    } as any,
  );

  const { data, refetch, isLoading, isFetching } = useCompaniesData(
    company,
    usdot,
    undefined,
    skip,
    '',
    ownerId,
    isDispute,
    uid,
  );

  const onChange = (query: any) => setSkip(String(query.current));

  const refresh = () => refetch();

  const handleChange = (event: string, inputName: string) => {
    const updateFunctions: Record<string, (value: string) => void> = {
      company: setCompany,
      usdot: setUsdot,
      uid: setUid,
    };

    if (updateFunctions[inputName]) {
      updateFunctions[inputName](event.trim());
      setSkip('1');
    }
  };

  const handleOwnerSelect = (value: any, { valId }: { valId: any }) => {
    setOwnerId(valId);
    setSkip('1');
  };

  const filtersUI = useMemo(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <SearchInput
          value={company}
          placeholder="Search by company"
          onDebounceChange={(event: string) => handleChange(event, 'company')}
          searchWidth="100%"
        />
        <SearchInput
          value={usdot}
          placeholder="Search by USDOT"
          onDebounceChange={(event: string) => handleChange(event, 'usdot')}
          searchWidth="100%"
        />
        <SearchOptions
          SearchResult={SearchForOwnerDispatchers}
          onSelect={handleOwnerSelect}
          placeholder="Search by owner"
          searchWidth="100%"
        />
        <SearchInput
          value={uid}
          placeholder="Search by UID"
          onDebounceChange={(event: string) => handleChange(event, 'uid')}
          searchWidth="100%"
        />

        {isMobile && (
          <Space style={{ width: '100%' }} size={10}>
            <Button block onClick={() => setFiltersOpen(false)}>
              Close
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                refresh();
                setFiltersOpen(false);
              }}
            >
              Apply & Refresh
            </Button>
          </Space>
        )}
      </div>
    ),
    [company, usdot, uid, isMobile],
  );

  return (
    <div className="mainPage">
      {/* Toolbar */}
      {!isMobile ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 10,
          }}
        >
          <SearchInput
            value={company}
            placeholder="Search by company"
            onDebounceChange={(event: string) => handleChange(event, 'company')}
          />

          <SearchInput
            value={usdot}
            placeholder="Search by USDOT"
            onDebounceChange={(event: string) => handleChange(event, 'usdot')}
          />

          <SearchOptions
            SearchResult={SearchForOwnerDispatchers}
            onSelect={handleOwnerSelect}
            placeholder="Search by owner"
          />

          <SearchInput
            value={uid}
            placeholder="Search by UID"
            onDebounceChange={(event: string) => handleChange(event, 'uid')}
          />

          {/* Actions right */}
          <div style={{ display: 'flex', gap: 10 }}>
            <Button size="large" onClick={refresh}>
              Refresh
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <Button
            block
            size="large"
            onClick={() => setFiltersOpen(true)}
            icon={<FilterOutlined />}
          />
          <Button block size="large" onClick={refresh}>
            Refresh
          </Button>
        </div>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title="Filters"
        placement="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        height="60vh"
      >
        {filtersUI}
      </Drawer>

      <CompaniesTable
        companiesData={data?.data}
        total={data?.count}
        onChange={onChange}
        isLoading={isLoading || isFetching}
        isFetching={isFetching}
        skip={skip}
        refetch={refetch}
      />
    </div>
  );
};

export default DisputedCompanies;
